import { useState, useCallback, useEffect, useRef, createContext, useContext, type ReactNode } from 'react'
import { X, CheckCircle2, AlertTriangle, AlertCircle, Info, Bell } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'notification'

export interface Toast {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

let toastId = 0
const TOAST_DURATION = 5000
const EXIT_DURATION = 350

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [dismissing, setDismissing] = useState<Set<string>>(new Set())

  const scheduleRemoval = useCallback((id: string) => {
    // Start exit animation
    setDismissing((prev) => new Set(prev).add(id))
    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
      setDismissing((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }, EXIT_DURATION)
  }, [])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = String(++toastId)
    setToasts((prev) => [...prev, { ...toast, id }])
    setTimeout(() => scheduleRemoval(id), TOAST_DURATION)
  }, [scheduleRemoval])

  const removeToast = useCallback((id: string) => {
    scheduleRemoval(id)
  }, [scheduleRemoval])

  return (
    <ToastContext value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastStack dismissing={dismissing} />
    </ToastContext>
  )
}

const variantConfig: Record<ToastVariant, { bg: string; border: string; color: string; progressColor: string; Icon: typeof CheckCircle2 }> = {
  success:      { bg: '#f0fdf4', border: '#bbf7d0', color: '#0a7c4e', progressColor: '#22c55e', Icon: CheckCircle2 },
  error:        { bg: '#fef2f2', border: '#fecaca', color: '#c61940', progressColor: '#ef4444', Icon: AlertCircle },
  warning:      { bg: '#fffbeb', border: '#fde68a', color: '#b45309', progressColor: '#f59e0b', Icon: AlertTriangle },
  info:         { bg: '#eff6ff', border: '#bfdbfe', color: '#0369a1', progressColor: '#3b82f6', Icon: Info },
  notification: { bg: '#f5f3ff', border: '#c4b5fd', color: '#6d28d9', progressColor: '#8b5cf6', Icon: Bell },
}

function ToastItem({ toast, isDismissing, onRemove }: { toast: Toast; isDismissing: boolean; onRemove: () => void }) {
  const config = variantConfig[toast.variant]
  const progressRef = useRef<HTMLDivElement>(null)
  const [paused, setPaused] = useState(false)

  // Trigger the progress bar animation after mount
  useEffect(() => {
    const el = progressRef.current
    if (!el) return
    // Force reflow then start animation
    el.style.transform = 'scaleX(1)'
    requestAnimationFrame(() => {
      el.style.transition = `transform ${TOAST_DURATION}ms linear`
      el.style.transform = 'scaleX(0)'
    })
  }, [])

  // Pause/resume progress bar on hover
  useEffect(() => {
    const el = progressRef.current
    if (!el) return
    el.style.animationPlayState = paused ? 'paused' : 'running'
    // Also pause the CSS transition
    if (paused) {
      const computed = getComputedStyle(el)
      el.style.transition = 'none'
      el.style.transform = computed.transform
    } else {
      el.style.transition = `transform ${TOAST_DURATION}ms linear`
      el.style.transform = 'scaleX(0)'
    }
  }, [paused])

  return (
    <div
      className={cn('relative flex items-start gap-3 rounded-2xl border-[1.5px] px-5 py-4 overflow-hidden')}
      style={{
        backgroundColor: config.bg,
        borderColor: config.border,
        boxShadow: `0 4px 20px -2px ${config.color}18, 0 2px 8px -2px ${config.color}10`,
        animation: isDismissing
          ? `gc-toast-out ${EXIT_DURATION}ms ease-in forwards`
          : 'gc-toast-in 300ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
      }}
      role="alert"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <config.Icon size={20} style={{ color: config.color, flexShrink: 0, marginTop: 1 }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold" style={{ color: config.color, letterSpacing: '-0.01em' }}>
          {toast.title}
        </p>
        {toast.description && (
          <p className="mt-0.5 text-xs leading-relaxed" style={{ color: config.color, opacity: 0.8 }}>
            {toast.description}
          </p>
        )}
      </div>
      <button
        onClick={onRemove}
        className="flex-shrink-0 p-1 rounded-lg transition-colors duration-150 hover:bg-black/5"
        style={{ color: config.color, opacity: 0.5 }}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
      {/* Progress bar */}
      <div
        ref={progressRef}
        className="absolute bottom-0 left-0 right-0 h-[3px] origin-left"
        style={{ backgroundColor: config.progressColor, opacity: 0.4 }}
      />
    </div>
  )
}

function ToastStack({ dismissing }: { dismissing: Set<string> }) {
  const { toasts, removeToast } = useToast()
  if (toasts.length === 0) return null

  return (
    <div
      className="fixed right-6 top-6 z-[var(--z-toast)] flex flex-col gap-3"
      style={{ width: 380 }}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          isDismissing={dismissing.has(toast.id)}
          onRemove={() => removeToast(toast.id)}
        />
      ))}
    </div>
  )
}
